import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Plus, RefreshCw, Pencil, AlertTriangle } from 'lucide-react';
import { pmApi, type WorkstreamDetail as WsDetail, type Task } from './pmApi';
import {
  StateLabel, RiskLevel, ReasonList, Stat, EmptyState, LoadingBlock,
  fmtDate, cleanName, cleanBlocker,
} from './components';
import TaskDialog from './TaskDialog';
import { Card, CardContent, CardHeader, CardTitle } from '../../../components/new-components/ui/card';
import { Button } from '../../../components/new-components/ui/button';
import { Badge } from '../../../components/new-components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../components/new-components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../../components/new-components/ui/table';

/**
 * Program view. The question is not "how much is done" but "can this stream
 * hit its terminal outcome by its target date" — so milestones, blockers, and
 * missing next actions come before the task list, and the list is ordered by
 * risk rather than by date.
 */
export default function WorkstreamDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [data, setData] = useState<WsDetail | null>(null);
  const [owners, setOwners] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Task | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const [res, o] = await Promise.all([pmApi.getWorkstream(id), pmApi.getOwners()]);
      if (res.success && res.data) setData(res.data);
      else setError(res.error || 'Workstream not found.');
      if (o.success && o.data) setOwners(o.data.map((x) => x.role_code));
    } catch {
      setError('Could not load this workstream.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  if (loading && !data) {
    return <Card className="border-border"><CardContent className="p-0"><LoadingBlock /></CardContent></Card>;
  }
  if (error) {
    return (
      <p className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700 dark:border-red-900 dark:bg-red-950/40 dark:text-red-400">
        {error}
      </p>
    );
  }
  if (!data) return null;

  const blocked = data.tasks.filter((t) => t.blocked && t.statusNormalized !== 'done');
  const missingNextAction = data.tasks.filter((t) => !t.nextAction && t.statusNormalized !== 'done');

  const openEdit = (t: Task) => { setEditing(t); setDialogOpen(true); };
  const openNew = () => { setEditing(null); setDialogOpen(true); };

  return (
    <div className="min-w-0 space-y-5 sm:space-y-6">
      <Button variant="ghost" size="sm" onClick={() => navigate('/admin/command')} className="-ml-2 gap-1">
        <ArrowLeft className="h-4 w-4" /> Operational Command
      </Button>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-3">
            <h1 className="text-xl font-display font-semibold text-[#242424] dark:text-foreground sm:text-2xl">{data.name}</h1>
            <StateLabel state={data.health} />
          </div>
          <p className="mt-1 text-sm text-muted-foreground">
            Executive owner · {data.executiveOwner || 'unassigned'} · target {fmtDate(data.targetDate)}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" size="sm" onClick={load} disabled={loading} className="gap-1">
            <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
          </Button>
          <Button size="sm" onClick={openNew} className="gap-2"><Plus className="h-4 w-4" /> Add to plan</Button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
        <Stat label="Open" value={data.stats.open} sub={`${data.stats.total} total`} />
        <Stat label="Overdue" value={data.stats.overdue} sub={`${data.stats.overdueCritical} critical`} tone={data.stats.overdue ? 'risk' : 'ok'} />
        <Stat label="Blocked" value={data.stats.blocked} tone={data.stats.blocked ? 'breach' : 'ok'} />
        <Stat label="Milestones" value={data.stats.milestones} sub={`${data.stats.breachedMilestones} breached`} tone={data.stats.breachedMilestones ? 'breach' : 'ok'} />
        <Stat label="No next action" value={missingNextAction.length} tone={missingNextAction.length ? 'risk' : 'ok'} />
        <Stat label="Reported complete" value={`${data.stats.percentComplete}%`} sub="context only" />
      </div>

      <Card className="border-border">
        <CardHeader className="pb-3"><CardTitle className="text-base">Why {data.health}</CardTitle></CardHeader>
        <CardContent><ReasonList reasons={data.healthReasons} /></CardContent>
      </Card>

      <Tabs defaultValue="work" className="space-y-4">
        <TabsList className="flex h-auto w-full flex-wrap justify-start gap-1">
          <TabsTrigger value="work">Work <Badge variant="secondary" className="ml-1.5 text-[10px]">{data.tasks.length}</Badge></TabsTrigger>
          {data.chain.links.length ? <TabsTrigger value="milestones">Milestones</TabsTrigger> : null}
          {blocked.length ? <TabsTrigger value="blockers">Blockers <Badge variant="destructive" className="ml-1.5 text-[10px]">{blocked.length}</Badge></TabsTrigger> : null}
          {data.dependencies.length ? <TabsTrigger value="dependencies">Dependencies</TabsTrigger> : null}
        </TabsList>

        <TabsContent value="work">
          <Card className="border-border">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="min-w-[280px]">Item</TableHead>
                      <TableHead className="w-24">Owner</TableHead>
                      <TableHead className="w-20">Due</TableHead>
                      <TableHead className="w-16">%</TableHead>
                      <TableHead className="w-24">Risk</TableHead>
                      <TableHead className="min-w-[200px]">Next action</TableHead>
                      <TableHead className="w-12" />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.tasks.map((t) => (
                      <TableRow key={t.id}>
                        <TableCell className="align-top">
                          <div className="flex flex-wrap items-center gap-2">
                            <span className="font-medium text-[#242424] dark:text-foreground">{cleanName(t.name)}</span>
                            {t.isMilestone ? <Badge variant="secondary" className="text-[10px]">Milestone</Badge> : null}
                            {t.blocked ? <Badge variant="destructive" className="text-[10px]">Blocked</Badge> : null}
                          </div>
                          {t.phase ? <p className="mt-0.5 text-xs text-muted-foreground">{cleanName(t.phase)}</p> : null}
                          <ReasonList reasons={t.risk.reasons} max={2} className="mt-1.5" />
                        </TableCell>
                        <TableCell className="align-top text-sm">{t.owner || '—'}</TableCell>
                        <TableCell className="align-top text-sm tabular-nums">{fmtDate(t.dueDate)}</TableCell>
                        <TableCell className="align-top text-sm tabular-nums">{t.percentComplete}%</TableCell>
                        <TableCell className="align-top"><RiskLevel level={t.risk.level} /></TableCell>
                        <TableCell className="align-top">
                          {t.nextAction ? <span className="text-sm">{t.nextAction}</span> : (
                            <Button variant="outline" size="sm" className="h-7 text-xs text-orange-700 dark:text-orange-400" onClick={() => openEdit(t)}>
                              Set next action
                            </Button>
                          )}
                        </TableCell>
                        <TableCell className="align-top text-right">
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0" onClick={() => openEdit(t)} aria-label="Edit">
                            <Pencil className="h-3.5 w-3.5" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              {!data.tasks.length ? <EmptyState title="No work in this workstream" /> : null}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="milestones">
          <Card className="border-border">
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Milestones</CardTitle>
              <p className="text-sm text-muted-foreground">A milestone cannot report higher confidence than the weakest link above it.</p>
            </CardHeader>
            <CardContent className="p-0">
              <div className="divide-y divide-border">
                {data.chain.links.map((l) => (
                  <div key={l.id} className="flex gap-3 p-4">
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-medium text-[#242424] dark:text-foreground">{l.cleanName}</p>
                      <p className="mt-0.5 text-xs tabular-nums text-muted-foreground">
                        {l.ownerRole ? `${l.ownerRole} · ` : ''}due {fmtDate(l.dueDate)} · {l.percentComplete}%
                      </p>
                      <ReasonList reasons={l.reasons} className="mt-1.5" />
                    </div>
                    <StateLabel state={l.confidence} className="shrink-0 self-start" />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="blockers">
          <Card className="border-border">
            <CardContent className="p-0">
              <div className="divide-y divide-border">
                {blocked.map((t) => (
                  <div key={t.id} className="flex gap-3 p-4">
                    <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-orange-500" />
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-[#242424] dark:text-foreground">{cleanName(t.name)}</p>
                      <p className="mt-0.5 text-xs text-muted-foreground">
                        {t.owner || 'No owner'} · due {fmtDate(t.dueDate)}
                      </p>
                      {t.blockerReason ? <p className="mt-1.5 text-sm text-foreground/75">{cleanBlocker(t.blockerReason)}</p> : null}
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="dependencies">
          <Card className="border-border">
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Dependencies</CardTitle>
              <p className="text-sm text-muted-foreground">
                Edges marked inferred were parsed from the plan&rsquo;s prose and have not been confirmed by a human.
              </p>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow><TableHead>Item</TableHead><TableHead>Waiting on</TableHead><TableHead className="w-28">State</TableHead><TableHead className="w-24">Source</TableHead></TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.dependencies.map((d) => (
                      <TableRow key={d.id}>
                        <TableCell className="font-medium">{cleanName(d.task_name)}</TableCell>
                        <TableCell className="text-sm">{d.depends_on_name ? cleanName(d.depends_on_name) : (d.unresolved_label || '—')}</TableCell>
                        <TableCell className="text-sm">
                          {d.depends_on_status === 'done' ? 'Resolved' : d.depends_on_name ? 'Outstanding' : 'Unmatched'}
                        </TableCell>
                        <TableCell><Badge variant="outline" className="text-[10px] font-normal">{d.confidence === 'parsed' ? 'Inferred' : 'Confirmed'}</Badge></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <TaskDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        task={editing}
        workstreams={[{ id: data.id, name: data.name } as never]}
        owners={owners}
        defaultWorkstreamId={data.id}
        onSaved={load}
      />
    </div>
  );
}
